import type { ChamberFigureState } from './ChamberFigure.tsx'

/**
 * 통합 콘솔 2D 챔버(`ChamberStageFigure`)의 **몸통 윤곽 · 무늬 한 벌** — 🔴 순수 함수(시험된다).
 *
 * 입력은 둘 다 서버에서 온 말이다 —
 *  - `state` — 챔버 상태 6종(`chamberFill()` · 배정 모름 · 중단/폐기 · 실패 · 담김)
 *  - `progress` — 챔버 진행 4종(`chamberProgress()` 의 `status` · 준비 중 · 진행 · 완료 · 실패)
 *
 * | 겉모습 | 몸통 윤곽 | 안 |
 * | --- | --- | --- |
 * | `unassigned` 배정 모름 | 옅은 **점점선** | 비었다(누구 것인지 모르는 우물에 공정을 그리지 않는다) |
 * | `halted` 중단 · 폐기 | 옅은 실선 | 안이 흐려지고 **가로 막대**가 가로지른다 |
 * | `fail` 실패 | **굵은 위험색 실선** | **대각 해칭**(실패 전용) |
 * | `pending` 준비 중 | **점선**(「아직 없음」 한 뜻) | 그 단계의 모습 |
 * | `active` 진행 | **굵은 강조색 실선** | 그 단계의 모습 + 도구가 움직인다 |
 * | `done` 완료 · 담김 | 실선 | 그 단계의 모습(채움) |
 * | `stage` 그 단계에 와 있음 | 실선(완료와 같은 결 · 굵지 않다) | 그 단계의 모습 · 🔴 **도구 · 조절기가 안 선다** |
 *
 * 🔴 **`stage` 는 「진행」이 아니다**(eric 2026-10-06 「시료별 하나씩 상태 바뀌게」). 서버가 그 단계의 진행을 «모듈 단위»로만
 *    말할 때(원심 · 어셈블러 — 어느 챔버를 처리 중인지 안 준다 · BR-98) 챔버마다 「진행」을 칠하면 그 순간 실제로 안 도는
 *    챔버까지 굵은 테 · 숨쉬는 도구가 함께 선다 — 아홉 칸이 같이 움직이는 거짓이다. 그래서 「이 단계에 와 있다」까지만 그린다.
 *    서버가 웰을 가리켜 말하면(`basis: 'well'`) 부르는 쪽이 종전 넷(준비 중 · 진행 · 완료 · 실패)으로 돌아간다.
 *
 * 🔴 **색만으로 가르지 않는다** — 여섯은 윤곽 모양(점점선 · 점선 · 실선 · 굵은 실선)과 무늬(해칭 · 막대)만으로도
 *    서로 다르다. 색(강조 · 위험)은 그 위에 얹힌다.
 */
export type ChamberStageLook =
  'unassigned' | 'halted' | 'fail' | 'pending' | 'active' | 'done' | 'stage'

/**
 * 챔버 진행 4종 — `chamberProgress.ts` `ChamberProgressStatus` 와 같은 어휘(그림 층은 화면 층을 들여오지 않는다) —
 * 에 **`stage`(그 단계에 와 있음 · 챔버별 진행 모름)** 하나를 더했다(위 표 · `BundleChamber` 가 고른다).
 */
export type ChamberStageProgress = 'pending' | 'active' | 'done' | 'failed' | 'stage'

/**
 * 🔴 **순서가 곧 우선순위다** — 배정 모름 > 중단 > 실패 > 진행 어휘 > 상태.
 *    진행 어휘가 없으면(배치 표지가 없다) 종전 상태 6종이 말한다.
 */
export function chamberStageLook(
  state: ChamberFigureState,
  progress?: ChamberStageProgress,
): ChamberStageLook {
  if (state === 'unassigned') return 'unassigned'
  if (state === 'halted') return 'halted'
  if (state === 'fail' || progress === 'failed') return 'fail'
  if (progress !== undefined) return progress
  switch (state) {
    case 'pending':
      return 'pending'
    case 'active':
      return 'active'
    default:
      return 'done'
  }
}
